  import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { debounce } from "lodash";
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Drawer,
  Empty,
  Input,
  Modal,
  Popover,
  Progress,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  ExportOutlined,
  EyeOutlined,
  FilterOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  SyncOutlined,
  SwapOutlined,
  SettingOutlined,
  UserOutlined,
  WalletOutlined,
  StopOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import moment from "moment";
import config from "../config";
import "./ReplacementSheetPage.css";

const { Text, Title } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const CARRIERS = ["Delhivery Surface", "DTDC Shipping", "Amazon Delivery", "Bus Transport", "Standard Delivery"];
const CARRIER_COLORS = { "Delhivery Surface": "#e11d48", "DTDC Shipping": "#2563eb", "Amazon Delivery": "#f59e0b", "Bus Transport": "#7c3aed", "Standard Delivery": "#059669" };

const STATUS_CONFIG = {
  pending: { label: "Waiting", color: "#92400e", bg: "#fef3c7", icon: <ClockCircleOutlined /> },
  email_sent: { label: "Email Sent", color: "#1e40af", bg: "#dbeafe", icon: <MailOutlined /> },
  processing: { label: "Processing", color: "#3730a3", bg: "#e0e7ff", icon: <SwapOutlined /> },
  completed: { label: "Completed", color: "#065f46", bg: "#d1fae5", icon: <CheckCircleOutlined /> },
  refund_only: { label: "Refund Done", color: "#9d174d", bg: "#fce7f3", icon: <WalletOutlined /> },
  rejected: { label: "Rejected", color: "#b91c1c", bg: "#fee2e2", icon: <StopOutlined /> },
  canceled: { label: "Canceled", color: "#6b7280", bg: "#f3f4f6", icon: <CloseCircleOutlined /> },
};

const ReplacementSheetPage = ({ darkTheme = false, isMobile = false }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [updatedDateRange, setUpdatedDateRange] = useState(null);
  const [callStatusFilter, setCallStatusFilter] = useState(null);
  const [carrierFilter, setCarrierFilter] = useState(null);
  const [issueFilter, setIssueFilter] = useState(null);
  const [actionTypeFilter, setActionTypeFilter] = useState(null);
  const [assignedToFilter, setAssignedToFilter] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [visibleColumns, setVisibleColumns] = useState([
    "sno", "oldOrderID", "createdAt", "oldAWB", "carrierName", "issue", "totalValue",
    "customer", "status", "selectedForAction", "newOrderID", "newAWB", "newCarrierName",
    "newValue", "callStatus", "assignedTo", "view",
  ]);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailActionType, setEmailActionType] = useState("replacement");
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailIssueNote, setEmailIssueNote] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardOrders, setWizardOrders] = useState([]);
  const [wizardIndex, setWizardIndex] = useState(0);
  const [wizardForm, setWizardForm] = useState({ newOrderID: "", newAWB: "", newCarrierName: "", replacementNote: "", newValue: "", isRefundOnly: false, isCreditNoteOnly: false, isRejected: false, isCanceled: false });
  const [processingSingle, setProcessingSingle] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [detailModal, setDetailModal] = useState({ open: false, order: null });
  const [callResult, setCallResult] = useState('');
  const [callNote, setCallNote] = useState('');
  const [callSaving, setCallSaving] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSearchID, setAddSearchID] = useState("");
  const [addSearching, setAddSearching] = useState(false);
  const [addFoundOrder, setAddFoundOrder] = useState(null);
  const [addForm, setAddForm] = useState({ issue: "", newOrderID: "", newAWB: "", newCarrierName: "", newValue: "", replacementNote: "", actionType: "replacement" });
  const [addSaving, setAddSaving] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assignmentNames, setAssignmentNames] = useState([]);
  const [assignmentDraft, setAssignmentDraft] = useState([]);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferFrom, setTransferFrom] = useState(null);
  const [transferTo, setTransferTo] = useState(null);
  const [transferSaving, setTransferSaving] = useState(false);
  const [assigningOrderId, setAssigningOrderId] = useState(null);
  const [autoAssigning, setAutoAssigning] = useState(false);

  const currentUserEmail = Cookies.get("userEmail") || "";
  const currentUserName = Cookies.get("userName") || "";

  const debouncedSetSearch = useRef(debounce((val) => setSearch(val), 300)).current;

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (carrierFilter) params.carrier = carrierFilter;
      if (issueFilter) params.issue = issueFilter;
      if (actionTypeFilter) params.actionType = actionTypeFilter;
      if (assignedToFilter) params.assignedTo = assignedToFilter;
      if (dateRange && dateRange[0]) params.startDate = dateRange[0].startOf("day").toISOString();
      if (dateRange && dateRange[1]) params.endDate = dateRange[1].endOf("day").toISOString();
      if (updatedDateRange && updatedDateRange[0]) params.updatedStartDate = updatedDateRange[0].startOf("day").toISOString();
      if (updatedDateRange && updatedDateRange[1]) params.updatedEndDate = updatedDateRange[1].endOf("day").toISOString();
      if (callStatusFilter) params.callStatus = callStatusFilter;
      const res = await axios.get(`${config.apiBaseUrl}/api/replacement-sheet`, { params });
      setRecords(res.data?.data || []);
    } catch (err) { message.error(err.response?.data?.message || "Failed to load records"); }
    finally { setLoading(false); }
  }, [search, statusFilter, dateRange, updatedDateRange, callStatusFilter, carrierFilter, issueFilter, actionTypeFilter, assignedToFilter]);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try { const res = await axios.get(`${config.apiBaseUrl}/api/replacement-sheet/summary`); setSummary(res.data?.data); }
    catch (err) { console.error("Summary error:", err); }
    finally { setLoadingSummary(false); }
  }, []);

  const fetchAssignmentConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${config.apiBaseUrl}/api/replacement-sheet/assignment-config`);
      const names = res.data?.data?.assignees || [];
      setAssignmentNames(names);
      setAssignmentDraft(names);
    } catch (err) { message.error(err.response?.data?.message || "Failed to load assignment settings"); }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchAssignmentConfig(); }, [fetchAssignmentConfig]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, carrierFilter, issueFilter, actionTypeFilter, assignedToFilter, dateRange, updatedDateRange]);

  const handleSyncFromRTO = async () => {
    setSyncing(true);
    try {
      const syncRes = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/sync-from-rto`, { limit: 500 });
      if (syncRes.data.success) message.success(`✅ ${syncRes.data.message}`);
      try { const valRes = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/fetch-values`, {}); if (valRes.data.success && valRes.data.updated > 0) message.success(`💰 Values fetched for ${valRes.data.updated} order(s)`); } catch (e) {}
      fetchRecords(); fetchSummary();
    } catch (err) { message.error(err.response?.data?.message || "Failed to sync"); }
    finally { setSyncing(false); }
  };

  const selectedOrders = useMemo(() => records.filter((r) => selectedRowKeys.includes(r._id)), [records, selectedRowKeys]);

  const allSelectedCompleted = useMemo(() => {
    if (!selectedRowKeys.length) return false;
    return selectedOrders.every((o) => o.status === "completed" || o.status === "refund_only");
  }, [selectedOrders, selectedRowKeys]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter) count++;
    if (carrierFilter) count++;
    if (issueFilter) count++;
    if (actionTypeFilter) count++;
    if (assignedToFilter) count++;
    if (dateRange) count++;
    if (updatedDateRange) count++;
    if (callStatusFilter) count++;
    return count;
  }, [statusFilter, carrierFilter, issueFilter, actionTypeFilter, assignedToFilter, dateRange, updatedDateRange]);

  const clearAllFilters = () => { setStatusFilter(null); setCarrierFilter(null); setIssueFilter(null); setActionTypeFilter(null); setAssignedToFilter(null); setDateRange(null); setUpdatedDateRange(null); setSearch(""); };

  const statCards = [
    { key: "total", status: null, label: "Total Orders", value: summary?.total || 0, suffix: `₹${(summary?.totalValue || 0).toLocaleString('en-IN')}`, icon: <BarChartOutlined />, gradient: "linear-gradient(135deg, #6366f1, #4f46e5)", clickable: true },
    { key: "pending", status: "pending", label: "Waiting", value: summary?.pending || 0, suffix: `Avg ₹${summary?.avgValue || 0}`, icon: <ClockCircleOutlined />, gradient: "linear-gradient(135deg, #f59e0b, #d97706)", clickable: true, warning: (summary?.pending || 0) > 5 },       
    { key: "completed", status: "completed", label: "Completed", value: summary?.completed || 0, icon: <CheckCircleOutlined />, gradient: "linear-gradient(135deg, #10b981, #059669)", clickable: true },
    { key: "refund_only", status: "refund_only", label: "Refund", value: summary?.refundOnly || 0, icon: <WalletOutlined />, gradient: "linear-gradient(135deg, #ec4899, #db2777)", clickable: true },
    { key: "rejected", status: "rejected", label: "Rejected", value: summary?.rejected || 0, icon: <StopOutlined />, gradient: "linear-gradient(135deg, #ef4444, #b91c1c)", clickable: true },
    { key: "canceled", status: "canceled", label: "Canceled", value: summary?.canceled || 0, icon: <CloseCircleOutlined />, gradient: "linear-gradient(135deg, #6b7280, #4b5563)", clickable: true },
  ];

  const carrierSummary = summary?.byCarrier || [];
  const assigneeSummary = summary?.byAssignee || [];

  const assignmentOptions = [
    ...assignmentNames.map((name) => ({ label: `${name} (${assigneeSummary.find((item) => item.assignee === name)?.count || 0})`, value: name })),
    ...(assigneeSummary.some((item) => item.assignee === "Unassigned") ? [{ label: `Unassigned (${assigneeSummary.find((item) => item.assignee === "Unassigned")?.count || 0})`, value: "__unassigned__" }] : []),
  ];

  const saveAssignmentConfig = async () => {
    const names = [...new Set(assignmentDraft.map((name) => String(name || "").trim()).filter(Boolean))];
    setAssignmentSaving(true);
    try {
      const res = await axios.put(`${config.apiBaseUrl}/api/replacement-sheet/assignment-config`, { assignees: names });
      const savedNames = res.data?.data?.assignees || names;
      setAssignmentNames(savedNames);
      setAssignmentDraft(savedNames);
      setAssignmentModalOpen(false);
      message.success("Waiting-order team saved");
      fetchSummary();
    } catch (err) { message.error(err.response?.data?.message || "Failed to save assignment settings"); }
    finally { setAssignmentSaving(false); }
  };

  const transferWaitingOrders = async () => {
    if (!transferFrom || !transferTo || transferFrom === transferTo) {
      message.warning("Choose different source and destination team members");
      return;
    }
    setTransferSaving(true);
    try {
      const res = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/assignment-transfer`, { from: transferFrom, to: transferTo });
      message.success(res.data?.message || "Waiting orders transferred");
      setTransferModalOpen(false);
      setTransferFrom(null);
      setTransferTo(null);
      fetchRecords();
      fetchSummary();
    } catch (err) { message.error(err.response?.data?.message || "Failed to transfer waiting orders"); }
    finally { setTransferSaving(false); }
  };

  const assignWaitingOrder = async (orderId, assignee) => {
    if (!assignee) return;
    setAssigningOrderId(orderId);
    try {
      const res = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/assignment-assign`, { orderId, assignee });
      message.success(res.data?.message || "Waiting order assigned");
      fetchRecords();
      fetchSummary();
    } catch (err) { message.error(err.response?.data?.message || "Failed to assign waiting order"); }
    finally { setAssigningOrderId(null); }
  };

  const autoAssignWaitingOrders = async () => {
    if (!assignmentNames.length) {
      message.warning("Configure the waiting-order team first");
      return;
    }
    setAutoAssigning(true);
    try {
      const res = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/assignment-auto-assign`);
      message.success(res.data?.message || "Waiting orders assigned");
      fetchRecords();
      fetchSummary();
    } catch (err) { message.error(err.response?.data?.message || "Failed to assign waiting orders"); }
    finally { setAutoAssigning(false); }
  };

  const columns = [
    { title: "S.No", key: "sno", width: 60, render: (_, __, index) => index + 1 },
    { title: "Old Order ID", dataIndex: "oldOrderID", key: "oldOrderID", width: 120, fixed: "left", className: "rs-order-id-column", render: (v) => <Text strong style={{ color: "#2563eb" }}>{v || "-"}</Text> },
    { title: "Created", dataIndex: "createdAt", key: "createdAt", width: 145, className: "rs-created-column", render: (v) => v ? moment(v).format("DD MMM YY HH:mm") : "-" },
    { title: "Old AWB", dataIndex: "oldAWB", key: "oldAWB", width: 140, render: (v) => <Text copyable>{v || "—"}</Text> },
    { title: "Carrier", dataIndex: "carrierName", key: "carrierName", width: 130, render: (v) => v ? <Tag color={CARRIER_COLORS[v] || "default"}>{v}</Tag> : "—" },
    { title: "Issue", dataIndex: "issue", key: "issue", width: 120, render: (v) => <Tag color="orange">{v || "RTO Returned"}</Tag> },
    { title: "Value", dataIndex: "totalValue", key: "totalValue", width: 90, render: (v) => <Text strong style={{ color: "#059669" }}>{v ? `₹${v}` : "—"}</Text> },
    { title: "Customer", dataIndex: ["customer", "name"], key: "customer", width: 140, render: (v) => v || "—" },
    { title: "Assigned To", dataIndex: "assignedTo", key: "assignedTo", width: 150, render: (v, record) => record.status === "pending" ? <Select size="small" loading={assigningOrderId === record._id} value={v || undefined} placeholder="Assign" options={assignmentNames.map((name) => ({ label: name, value: name }))} onChange={(assignee) => assignWaitingOrder(record._id, assignee)} style={{ width: 125 }} /> : (v || <Text type="secondary">Unassigned</Text>) },
    { title: "Status", dataIndex: "status", key: "status", width: 110, render: (v) => { const cfg = STATUS_CONFIG[v] || { label: v, color: "#666", bg: "#f1f5f9" }; return <span className="rs-status-tag" style={{ background: cfg.bg, color: cfg.color }}>{cfg.icon} {cfg.label}</span>; } },
    { title: "Action", dataIndex: "selectedForAction", key: "selectedForAction", width: 110, render: (v) => { const actionMap = { replacement: { label: "Replacement", color: "#0f766e", bg: "#f0fdfa" }, credit_note: { label: "Credit Note", color: "#d97706", bg: "#fffbeb" }, refund: { label: "Refund", color: "#db2777", bg: "#fdf2f8" } }; const cfg = actionMap[v]; return cfg ? <span style={{ background: cfg.bg, color: cfg.color, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{cfg.label}</span> : "—"; } },
    { title: "New Order ID", dataIndex: "newOrderID", key: "newOrderID", width: 120, render: (v) => v ? <Text strong style={{ color: "#2563eb" }}>{v}</Text> : "—" },
    { title: "New AWB", dataIndex: "newAWB", key: "newAWB", width: 120, render: (v) => v || "—" },
    { title: "New Carrier", dataIndex: "newCarrierName", key: "newCarrierName", width: 130, render: (v) => v || "—" },
    { title: "New Value", dataIndex: "newValue", key: "newValue", width: 90, render: (v) => v ? <Text strong style={{ color: "#059669" }}>₹{v}</Text> : "—" },
    { title: "Call", dataIndex: "callStatus", key: "callStatus", width: 120, render: (v) => {
      const value = v || 'not_called';
      const color = value === 'called' ? 'green' : value === 'no_answer' ? 'orange' : value === 'callback_requested' ? 'blue' : 'red';
      const label = value === 'not_called' ? 'Not Called' : value === 'called' ? 'Called' : value === 'no_answer' ? 'No Answer' : 'Callback Requested';
      return <Tag color={color}>{label}</Tag>;
    } },
    { title: "", key: "view", width: 45, render: (_, r) => <Tooltip title="View Details"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setDetailModal({ open: true, order: r })} /></Tooltip> },
  ];
  const displayedColumns = columns.filter((column) => visibleColumns.includes(column.key));
  const columnOptions = columns.map((column) => ({
    label: column.title || "Details",
    value: column.key,
  }));

  const openEmailModal = (actionType) => {
    if (!selectedRowKeys.length) { message.warning("Please select at least one order"); return; }
    setEmailActionType(actionType);
    setEmailSubject({ credit_note: "Credit Note Approval Required", replacement: "Replacement Approval Required", refund: "Refund Approval Required" }[actionType] || "");
    setEmailTo(""); setEmailCc(""); setEmailIssueNote(""); setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo.trim()) { message.error("Please enter at least one recipient email"); return; }
    if ((emailActionType === "credit_note" || emailActionType === "refund") && !emailIssueNote.trim()) { message.error("Issue note is required for Credit Note / Refund"); return; }
    setEmailSending(true);
    try {
      const res = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/send-email`, { orderIds: selectedRowKeys, actionType: emailActionType, to: emailTo, cc: emailCc, subject: emailSubject, issueNote: emailIssueNote });
      if (res.data.success) { message.success("📧 Email sent!"); setEmailModalOpen(false); setSelectedRowKeys([]); fetchRecords(); fetchSummary(); }
    } catch (err) { message.error(err.response?.data?.message || "Failed"); }
    finally { setEmailSending(false); }
  };

  const startWizard = () => {
    if (!selectedRowKeys.length) { message.warning("Please select at least one order"); return; }
    setWizardOrders([...selectedOrders]); setWizardIndex(0);
    setWizardForm({ newOrderID: "", newAWB: "", newCarrierName: "", replacementNote: "", newValue: "", isRefundOnly: false, isCreditNoteOnly: false, isRejected: false, isCanceled: false });
    setWizardOpen(true);
  };

  const processCurrentOrder = async () => {
    if (!currentWizardOrder) return;
    if (!wizardForm.isRefundOnly && !wizardForm.isCreditNoteOnly && !wizardForm.isRejected && !wizardForm.isCanceled) {
      if (!wizardForm.newOrderID.trim()) { message.error("New Order ID is required"); return; }
      if (!wizardForm.newAWB.trim()) { message.error("New AWB is required"); return; }
      if (!wizardForm.newCarrierName) { message.error("Please select a New Carrier"); return; }
    }
    setProcessingSingle(true);
    try {
      const res = await axios.put(`${config.apiBaseUrl}/api/replacement-sheet/${currentWizardOrder._id}/process`, {
        newOrderID: wizardForm.newOrderID, newAWB: wizardForm.newAWB, newCarrierName: wizardForm.newCarrierName,
        replacementNote: wizardForm.replacementNote, newValue: wizardForm.newValue ? Number(wizardForm.newValue) : 0,
        isRefundOnly: wizardForm.isRefundOnly, isCreditNoteOnly: wizardForm.isCreditNoteOnly, isRejected: wizardForm.isRejected, isCanceled: wizardForm.isCanceled, processedBy: currentUserName, processedByEmail: currentUserEmail,
      });
      if (res.data.success) {
        message.success(`Order ${currentWizardOrder.oldOrderID} processed!`);
        if (wizardIndex < wizardOrders.length - 1) { setWizardIndex(wizardIndex + 1); setWizardForm({ newOrderID: "", newAWB: "", newCarrierName: "", replacementNote: "", newValue: "", isRefundOnly: false, isCreditNoteOnly: false, isRejected: false, isCanceled: false }); }
        else { message.success("🎉 All done!"); setWizardOpen(false); setSelectedRowKeys([]); fetchRecords(); fetchSummary(); }
      }
    } catch (err) { message.error(err.response?.data?.message || "Failed"); }
    finally { setProcessingSingle(false); }
  };

  const currentWizardOrder = wizardOrders[wizardIndex];

  const handleExportExcel = async () => {
    if (!selectedRowKeys.length) { message.warning("Select orders to export"); return; }
    try {
      const res = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/export-excel`, { orderIds: selectedRowKeys }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", `replacements-${moment().format("YYYYMMDD-HHmm")}.xlsx`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
      message.success("Excel downloaded!");
    } catch (err) { message.error("Failed to export"); }
  };

  const handleExportSummary = async () => {
    try {
      const body = {};
      if (search) body.search = search;
      if (statusFilter) body.status = statusFilter;
      if (carrierFilter) body.carrier = carrierFilter;
      if (issueFilter) body.issue = issueFilter;
      if (actionTypeFilter) body.actionType = actionTypeFilter;
      if (dateRange && dateRange[0]) body.startDate = dateRange[0].startOf("day").toISOString();
      if (dateRange && dateRange[1]) body.endDate = dateRange[1].endOf("day").toISOString();
      if (updatedDateRange && updatedDateRange[0]) body.updatedStartDate = updatedDateRange[0].startOf("day").toISOString();
      if (updatedDateRange && updatedDateRange[1]) body.updatedEndDate = updatedDateRange[1].endOf("day").toISOString();
      if (callStatusFilter) body.callStatus = callStatusFilter;
      const res = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/export-summary`, body, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", `summary-${moment().format("YYYYMMDD-HHmm")}.xlsx`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
      message.success("Summary exported!");
    } catch (err) { message.error(err.response?.data?.message || "Failed to export summary"); }
  };

  const handleSearchOrder = async () => {
    if (!addSearchID.trim()) { message.warning("Enter an Order ID"); return; }
    setAddSearching(true); setAddFoundOrder(null);
    try { const res = await axios.post(`${config.apiBaseUrl}/orders/past-orders`, { orderID: addSearchID.trim() }); if (res.data.order) setAddFoundOrder(res.data.order); else message.warning("Order not found"); }
    catch (err) { message.error(err.response?.data?.message || "Order not found"); }
    finally { setAddSearching(false); }
  };

  const handleAddOrder = async () => {
    if (!addFoundOrder) { message.error("Search and select an order first"); return; }
    if (!addForm.issue.trim()) { message.error("Issue note is required"); return; }
    const isReplacement = addForm.actionType === "replacement";
    if (isReplacement) {
      if (!addForm.newOrderID.trim()) { message.error("New Order ID is required"); return; }
      if (!addForm.newAWB.trim()) { message.error("New AWB is required"); return; }
      if (!addForm.newCarrierName) { message.error("Please select a carrier"); return; }
    }
    setAddSaving(true);
    try {
      const order = addFoundOrder;
      const res = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/create-custom`, {
        oldOrderID: order.orderID || addSearchID, oldAWB: order.shipments?.[0]?.awb_code || "", carrierName: order.shipments?.[0]?.courier_name || "",
        issue: addForm.issue, totalValue: 0, customer: { name: order.customer?.name || "", email: order.customer?.email || "", mobile: order.customer?.mobile || "" },
        products: order.products || [],
        newOrderID: isReplacement ? addForm.newOrderID : "", newAWB: isReplacement ? addForm.newAWB : "", newCarrierName: isReplacement ? addForm.newCarrierName : "",
        newValue: isReplacement && addForm.newValue ? Number(addForm.newValue) : 0,
        replacementNote: addForm.replacementNote,
        isRefundOnly: addForm.actionType === "refund", isCreditNoteOnly: addForm.actionType === "credit_note",
        processedBy: currentUserName, processedByEmail: currentUserEmail,
      });
      if (res.data.success) { message.success("✅ Created!"); setAddModalOpen(false); setAddSearchID(""); setAddFoundOrder(null); setAddForm({ issue: "", newOrderID: "", newAWB: "", newCarrierName: "", newValue: "", replacementNote: "", actionType: "replacement" }); fetchRecords(); fetchSummary(); }
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.message;
      if (status === 409) {
        Modal.error({ title: 'Replacement exists', content: serverMsg || 'A replacement for this order already exists.' });
      } else {
        message.error(serverMsg || 'Failed');
      }
    }
    finally { setAddSaving(false); }
  };

  const rowSelection = { fixed: true, selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) };

  return (
    <div className={`rs-container ${darkTheme ? 'dark-theme' : ''}`}>
      <div className="rs-hero">
        <div className="rs-hero-left">
          <span className="rs-hero-eyebrow">RTO Replacements</span>
          <Title level={2} className="rs-hero-title">Replacements Module</Title>
        </div>
        <Input
          className="rs-header-search"
          prefix={<SearchOutlined />}
          placeholder="Search by Order ID, AWB, or Customer name"
          allowClear
          onChange={(e) => debouncedSetSearch(e.target.value)}
        />
        <div className="rs-hero-actions">
          <Button icon={<UserOutlined />} onClick={() => { setAssignmentDraft(assignmentNames); setAssignmentModalOpen(true); }}>Waiting Team</Button>
          <Button icon={<SwapOutlined />} onClick={() => { setTransferFrom(assignedToFilter && assignedToFilter !== "__unassigned__" ? assignedToFilter : null); setTransferTo(null); setTransferModalOpen(true); }}>Transfer Waiting</Button>
          <Button icon={<SyncOutlined />} loading={autoAssigning} onClick={autoAssignWaitingOrders}>Auto Assign Waiting</Button>
          <Button icon={<PlusOutlined />} onClick={() => { setAddSearchID(""); setAddFoundOrder(null); setAddForm({ issue: "", newOrderID: "", newAWB: "", newCarrierName: "", newValue: "", replacementNote: "", actionType: "replacement" }); setAddModalOpen(true); }} style={{ background: "#fff", borderColor: "#0f766e", color: "#0f766e" }}>Add Order</Button>
          <Button icon={<SyncOutlined />} loading={syncing} onClick={handleSyncFromRTO}>Sync from RTO</Button>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchRecords(); fetchSummary(); }}>Refresh</Button>
        </div>
      </div>

      <div className="rs-stat-grid">
        {statCards.map((s) => (
          <div
            key={s.key}
            className={`rs-stat-card ${s.clickable ? 'rs-stat-card-clickable' : ''} ${s.warning ? 'rs-stat-card-warning' : ''} ${statusFilter === s.status ? 'rs-stat-card-active' : ''}`}
            onClick={() => {
              if (!s.clickable) return;
              setStatusFilter(s.status === statusFilter ? null : s.status);
            }}
          >
            <div className="rs-stat-head"><span className="rs-stat-label">{s.label}</span><span className="rs-stat-icon" style={{ background: s.gradient }}>{s.icon}</span></div>
            <div className="rs-stat-value">{loadingSummary ? "—" : s.value}<span className="rs-stat-suffix">{s.suffix || "orders"}</span></div>
          </div>
        ))}
      </div>

      {carrierSummary.length > 0 && (
        <div className="rs-carrier-row">
          {carrierSummary.slice(0, 5).map((c) => (
            <div className="rs-carrier-chip" key={c.carrier} onClick={() => setCarrierFilter(carrierFilter === c.carrier ? null : c.carrier)}>
              <span className="rs-carrier-dot" style={{ background: CARRIER_COLORS[c.carrier] || "#888" }} />
              <Text strong style={{ fontSize: 12 }}>{c.carrier}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{c.count}</Text>
            </div>
          ))}
        </div>
      )}

      {assignmentNames.length > 0 && (
        <div className="rs-carrier-row">
          {assignmentNames.map((name) => {
            const count = assigneeSummary.find((item) => item.assignee === name)?.count || 0;
            const active = assignedToFilter === name;
            return (
              <div className="rs-carrier-chip" key={name} onClick={() => setAssignedToFilter(active ? null : name)} style={active ? { borderColor: "#1d5a45", background: "#e9f5ed" } : undefined}>
                <UserOutlined style={{ color: "#39835e" }} />
                <Text strong style={{ fontSize: 12 }}>{name}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>{count} waiting</Text>
              </div>
            );
          })}
        </div>
      )}

      <div className="rs-shell">
        <div className="rs-filters">
          <Select allowClear placeholder="Status" value={statusFilter || undefined} onChange={(v) => setStatusFilter(v || null)} style={{ minWidth: 140 }}
            options={Object.entries(STATUS_CONFIG).map(([k, v]) => ({ label: v.label, value: k }))} />
          <Select allowClear placeholder="Assigned To" value={assignedToFilter || undefined} onChange={(v) => setAssignedToFilter(v || null)} style={{ minWidth: 180 }} options={assignmentOptions} />
          <Select allowClear placeholder="Carrier" value={carrierFilter || undefined} onChange={(v) => setCarrierFilter(v || null)} style={{ minWidth: 160 }}
            options={carrierSummary.map((c) => ({ label: `${c.carrier} (${c.count})`, value: c.carrier }))} />
          <RangePicker value={dateRange} onChange={(values) => setDateRange(values)} allowClear style={{ minWidth: 240 }} />
          <RangePicker value={updatedDateRange} onChange={(values) => setUpdatedDateRange(values)} allowClear style={{ minWidth: 240, marginLeft: 8 }} placeholder={["Completed From", "Completed To"]} />
          <Select allowClear placeholder="Call Status" value={callStatusFilter || undefined} onChange={(v) => setCallStatusFilter(v || null)} style={{ minWidth: 160, marginLeft: 8 }} options={[{ label: 'Not Called', value: 'not_called' }, { label: 'Called', value: 'called' }, { label: 'No Answer', value: 'no_answer' }, { label: 'Callback Requested', value: 'callback_requested' }]} />
          <Select allowClear placeholder="Action Type" value={actionTypeFilter || undefined} onChange={(v) => setActionTypeFilter(v || null)} style={{ minWidth: 140 }} options={[{ label: "Replacement", value: "replacement" }, { label: "Credit Note", value: "credit_note" }, { label: "Refund", value: "refund" }]} />
          <Popover content={<div style={{ display: "flex", flexDirection: "column", gap: 8, width: 200 }}>
            <Input placeholder="Issue type" allowClear value={issueFilter || ""} onChange={(e) => setIssueFilter(e.target.value || null)} />
          </div>} trigger="click" placement="bottomRight">
            <Button icon={<FilterOutlined />} badge={activeFilterCount > 0 ? { count: activeFilterCount } : null}>More Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}</Button>
          </Popover>
          {activeFilterCount > 0 && <Button onClick={clearAllFilters} type="link" danger>Clear All</Button>}
          <Button icon={<DownloadOutlined />} onClick={handleExportSummary} style={{ borderColor: "#059669", color: "#059669", marginLeft: "auto" }}>Export Summary</Button>
        </div>

        {activeFilterCount > 0 && (
          <div className="rs-active-chips">
            {statusFilter && <Tag closable onClose={() => setStatusFilter(null)}>Status: {STATUS_CONFIG[statusFilter]?.label}</Tag>}
            {assignedToFilter && <Tag closable onClose={() => setAssignedToFilter(null)}>Assigned: {assignedToFilter === "__unassigned__" ? "Unassigned" : assignedToFilter}</Tag>}
            {carrierFilter && <Tag closable onClose={() => setCarrierFilter(null)}>Carrier: {carrierFilter}</Tag>}
{issueFilter && <Tag closable onClose={() => setIssueFilter(null)}>Issue: {issueFilter}</Tag>}
{actionTypeFilter && <Tag closable onClose={() => setActionTypeFilter(null)}>Action: {actionTypeFilter === "credit_note" ? "Credit Note" : actionTypeFilter === "refund" ? "Refund" : "Replacement"}</Tag>}
{dateRange && <Tag closable onClose={() => setDateRange(null)}>Date: {dateRange[0]?.format("DD MMM")} - {dateRange[1]?.format("DD MMM")}</Tag>}
{updatedDateRange && <Tag closable onClose={() => setUpdatedDateRange(null)}>Completed: {updatedDateRange[0]?.format("DD MMM")} - {updatedDateRange[1]?.format("DD MMM")}</Tag>}
{callStatusFilter && <Tag closable onClose={() => setCallStatusFilter(null)} color={callStatusFilter === 'not_called' ? 'red' : callStatusFilter === 'called' ? 'green' : callStatusFilter === 'no_answer' ? 'orange' : 'blue'}>Call: {callStatusFilter === 'not_called' ? 'Not Called' : callStatusFilter === 'called' ? 'Called' : callStatusFilter === 'no_answer' ? 'No Answer' : 'Callback Requested'}</Tag>}
          </div>
        )}

        {selectedRowKeys.length > 0 && (
          <div className="rs-action-bar">
            <span className="selected-count">{selectedRowKeys.length} selected</span>
            {!allSelectedCompleted && (
              <>
                <Button icon={<MailOutlined />} onClick={() => openEmailModal("credit_note")} style={{ borderColor: "#f59e0b", color: "#d97706" }}>Credit Note</Button>
                <Button type="primary" icon={<SendOutlined />} onClick={() => openEmailModal("replacement")}>Replacement Approval</Button>
                <Button icon={<WalletOutlined />} onClick={() => openEmailModal("refund")} style={{ borderColor: "#ec4899", color: "#db2777" }}>Refund</Button>
                <Divider type="vertical" />
                <Button type="primary" icon={<SwapOutlined />} onClick={startWizard} style={{ background: "#0f766e", borderColor: "#0f766e" }}>Start Process</Button>
              </>
            )}
            {allSelectedCompleted && <Text type="secondary" style={{ fontSize: 13 }}>All selected orders are completed — action options hidden</Text>}
            <Divider type="vertical" />
            <Button icon={<DownloadOutlined />} onClick={handleExportExcel}>Export Selected</Button>
          </div>
        )}

        <div className="rs-table-toolbar">
          <Text type="secondary">{records.length} replacement records</Text>
          <Popover
            placement="bottomRight"
            trigger="click"
            title="Show Columns"
            content={(
              <Checkbox.Group
                className="rs-column-options"
                options={columnOptions}
                value={visibleColumns}
                onChange={setVisibleColumns}
              />
            )}
          >
            <Button className="rs-columns-button" icon={<SettingOutlined />} aria-label="Show or hide table columns" title="Show or hide table columns" />
          </Popover>
        </div>

        <Table className="rs-table" rowKey="_id" rowSelection={rowSelection} columns={displayedColumns} dataSource={records} loading={loading}
          pagination={{ current: currentPage, pageSize, onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setCurrentPage(1); } else { setCurrentPage(p); } }, showSizeChanger: true, pageSizeOptions: ["15", "30", "50", "100"], showTotal: (t, r) => <Text type="secondary">{r[0]}-{r[1]} of {t} orders</Text> }}
          scroll={{ x: 1800 }}
          locale={{ emptyText: <div className="rs-empty-block"><Empty description="No orders match filters" /></div> }} />
      </div>
      {/* ORDER DETAIL DRAWER */}
      <Drawer className={`rs-detail-drawer ${darkTheme ? "dark-theme" : ""}`} placement="right"
        title={<div style={{ display: "flex", alignItems: "center", gap: 8 }}><EyeOutlined style={{ color: "#1d5a45" }} /><span>Order Details</span></div>}
        open={detailModal.open} onClose={() => setDetailModal({ open: false, order: null })} width={isMobile ? "100%" : 760}>
        {detailModal.order && (() => { const o = detailModal.order; return (
          <div>
            <div className="rs-detail-summary-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Old Order ID</Text><br /><Text strong style={{ fontSize: 16 }}>{o.oldOrderID}</Text></Card>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Status</Text><br /><span className="rs-status-tag" style={{ background: STATUS_CONFIG[o.status]?.bg, color: STATUS_CONFIG[o.status]?.color }}>{STATUS_CONFIG[o.status]?.icon} {STATUS_CONFIG[o.status]?.label}</span></Card>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Old AWB</Text><br /><Text>{o.oldAWB || "—"}</Text></Card>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Carrier</Text><br /><Tag color={CARRIER_COLORS[o.carrierName]}>{o.carrierName || "—"}</Tag></Card>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Customer</Text><br /><Text strong>{o.customer?.name || "—"}</Text><br /><Text type="secondary" style={{ fontSize: 11 }}>{o.customer?.email || "—"}</Text></Card>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Total Value</Text><br /><Text strong style={{ color: "#059669", fontSize: 18 }}>₹{o.totalValue || 0}</Text></Card>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Issue</Text><br /><Tag color="orange">{o.issue || "RTO Returned"}</Tag></Card>
              <Card size="small"><Text type="secondary" style={{ fontSize: 11 }}>Processed By</Text><br /><Text>{o.processedBy || "—"}</Text></Card>
            </div>
            <Divider />
            <div className="rs-detail-content-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
              <div className="rs-detail-call-history">
                {o.newOrderID ? (
                  <Card size="small" style={{ minHeight: 220 }}>
                    <Text strong style={{ color: "#065f46" }}>✅ Replacement Completed</Text>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
                      <div><Text type="secondary" style={{ fontSize: 11 }}>New Order ID</Text><br /><Text strong>{o.newOrderID}</Text></div>
                      <div><Text type="secondary" style={{ fontSize: 11 }}>New AWB</Text><br /><Text>{o.newAWB}</Text></div>
                      <div><Text type="secondary" style={{ fontSize: 11 }}>New Carrier</Text><br /><Tag>{o.newCarrierName}</Tag></div>
                    </div>
                    {o.newValue > 0 && <div style={{ marginTop: 12 }}><Text type="secondary" style={{ fontSize: 11 }}>New Value</Text><br /><Text strong style={{ color: "#059669", fontSize: 16 }}>₹{o.newValue}</Text></div>}
                    {o.replacementNote && <div style={{ marginTop: 12 }}><Text type="secondary" style={{ fontSize: 11 }}>Note</Text><br /><Text>{o.replacementNote}</Text></div>}
                  </Card>
                ) : (
                  <Card size="small" style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Text type="secondary">No replacement completed yet</Text></Card>
                )}
              </div>
              <div className="rs-detail-call-form">
                <Text strong style={{ fontSize: 13 }}>Call History</Text>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(o.callLogs || []).length === 0 && <Card size="small" style={{ minHeight: 140, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Text type="secondary">No calls logged</Text></Card>}
                  {(o.callLogs || []).slice().reverse().map((cl, idx) => (
                    <Card size="small" key={idx} style={{ background: '#fafafa' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <Text strong>{cl.result || 'Call'}</Text>
                          <div><Text type="secondary" style={{ fontSize: 12 }}>{cl.by || '—'}</Text></div>
                          {cl.note && <div style={{ marginTop: 6 }}><Text>{cl.note}</Text></div>}
                        </div>
                        <div style={{ textAlign: 'right' }}><Text type="secondary">{moment(cl.at).format('DD MMM YY HH:mm')}</Text></div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>Log a Call</Text>
                <Select placeholder="Result" value={callResult || undefined} onChange={(v) => setCallResult(v)} style={{ width: '100%', marginBottom: 8 }} options={[{ label: 'Called', value: 'called' }, { label: 'No Answer', value: 'no_answer' }, { label: 'Callback Requested', value: 'callback_requested' }]} />
                <TextArea rows={6} placeholder="Optional note" value={callNote} onChange={(e) => setCallNote(e.target.value)} style={{ marginBottom: 8 }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <Button onClick={() => { setCallResult(''); setCallNote(''); }}>Reset</Button>
                  <Button type="primary" loading={callSaving} onClick={async () => {
                    if (!o._id) return;
                    setCallSaving(true);
                    try {
                      const res = await axios.post(`${config.apiBaseUrl}/api/replacement-sheet/${o._id}/call-log`, { result: callResult, note: callNote, by: currentUserName, status: callResult });
                      if (res.data.success) { message.success('Call logged'); setCallResult(''); setCallNote(''); setDetailModal({ open: true, order: { ...o, callLogs: [...(o.callLogs||[]), res.data.data], callStatus: res.data.callStatus } }); fetchRecords(); }
                    } catch (err) { message.error(err.response?.data?.message || 'Failed to save call log'); }
                    finally { setCallSaving(false); }
                  }}>Save Call</Button>
                </div>
              </div>
            </div>
          </div>
        ); })()}
      </Drawer>
      {/* EMAIL MODAL */}
      <Modal title={<div style={{ display: "flex", alignItems: "center", gap: 8 }}><MailOutlined style={{ color: "#1890ff" }} /><span>Send {emailActionType === "credit_note" ? "Credit Note" : emailActionType === "refund" ? "Refund" : "Replacement"} Approval</span></div>}
        open={emailModalOpen} onCancel={() => setEmailModalOpen(false)} footer={null} width={640}>
        <div className="rs-email-orders-list"><Text strong style={{ fontSize: 13 }}>Selected ({selectedOrders.length}):</Text>
          {selectedOrders.map((o, i) => <div className="rs-email-order-item" key={o._id}><span>{i + 1}. {o.oldOrderID} | {o.oldAWB || "N/A"}</span><span style={{ fontWeight: 600, color: "#059669" }}>{o.totalValue ? `₹${o.totalValue}` : "—"}</span></div>)}
        </div>
        {(emailActionType === "credit_note" || emailActionType === "refund") && (
          <div style={{ marginBottom: 10 }}>
            <Text strong style={{ fontSize: 12 }}>Issue Note * <Text type="danger">(Required)</Text></Text>
            <TextArea rows={3} placeholder={`Why is this ${emailActionType === "credit_note" ? "Credit Note" : "Refund"} needed?`} value={emailIssueNote} onChange={(e) => setEmailIssueNote(e.target.value)} style={{ marginTop: 4 }} />
          </div>
        )}
        <div style={{ marginBottom: 10 }}><Text strong style={{ fontSize: 12 }}>To *</Text><Input placeholder="recipient@email.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} style={{ marginTop: 4 }} /></div>
        <div style={{ marginBottom: 10 }}><Text strong style={{ fontSize: 12 }}>CC</Text><Input placeholder="cc@email.com" value={emailCc} onChange={(e) => setEmailCc(e.target.value)} style={{ marginTop: 4 }} /></div>
        <div style={{ marginBottom: 10 }}><Text strong style={{ fontSize: 12 }}>Subject *</Text><Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} style={{ marginTop: 4 }} /></div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <Button onClick={() => setEmailModalOpen(false)}>Cancel</Button>
          <Button type="primary" icon={<SendOutlined />} loading={emailSending} onClick={handleSendEmail} style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none" }}>Send</Button>
        </div>
      </Modal>
      {/* WAITING TEAM CONFIGURATION */}
      <Modal
        title={<div style={{ display: "flex", alignItems: "center", gap: 8 }}><UserOutlined style={{ color: "#0f766e" }} /><span>Waiting Order Team</span></div>}
        open={assignmentModalOpen}
        onCancel={() => setAssignmentModalOpen(false)}
        onOk={saveAssignmentConfig}
        okText="Save Team"
        confirmLoading={assignmentSaving}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 10 }}>
          New waiting orders will be assigned equally in round-robin order.
        </Text>
        <Select
          mode="tags"
          style={{ width: "100%" }}
          placeholder="Enter team member names"
          value={assignmentDraft}
          onChange={setAssignmentDraft}
          tokenSeparators={[","]}
          open={false}
        />
        <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
          Current pending counts are available in the Assigned To filter.
        </Text>
      </Modal>
      {/* TRANSFER WAITING ORDERS */}
      <Modal
        title={<div style={{ display: "flex", alignItems: "center", gap: 8 }}><SwapOutlined style={{ color: "#0f766e" }} /><span>Transfer Waiting Orders</span></div>}
        open={transferModalOpen}
        onCancel={() => setTransferModalOpen(false)}
        onOk={transferWaitingOrders}
        okText="Transfer Orders"
        confirmLoading={transferSaving}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Transfer all pending waiting orders from one team member to another.
        </Text>
        <div style={{ display: "grid", gap: 12 }}>
          <Select placeholder="Transfer from" value={transferFrom || undefined} onChange={setTransferFrom} options={assignmentOptions.filter((option) => option.value !== "__unassigned__")} />
          <Select placeholder="Transfer to" value={transferTo || undefined} onChange={setTransferTo} options={assignmentNames.filter((name) => name !== transferFrom).map((name) => ({ label: name, value: name }))} />
        </div>
      </Modal>
      {/* ADD ORDER MODAL */}
      <Modal title={<div style={{ display: "flex", alignItems: "center", gap: 8 }}><PlusOutlined style={{ color: "#0f766e" }} /><span>Add Order</span></div>}
        open={addModalOpen} onCancel={() => setAddModalOpen(false)} footer={null} width={640}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <Input placeholder="Enter Order ID (e.g. S0265462)" value={addSearchID} onChange={(e) => setAddSearchID(e.target.value)} onPressEnter={handleSearchOrder} style={{ flex: 1 }} />
          <Button type="primary" icon={<SearchOutlined />} loading={addSearching} onClick={handleSearchOrder}>Search</Button>
        </div>
        {addFoundOrder && (
          <Card size="small" style={{ marginBottom: 16, background: "#f0fdf4", borderColor: "#bbf7d0" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div><Text type="secondary" style={{ fontSize: 11 }}>Order ID</Text><br /><Text strong>{addFoundOrder.orderID}</Text></div>
              <div><Text type="secondary" style={{ fontSize: 11 }}>Customer</Text><br /><Text strong>{addFoundOrder.customer?.name || "—"}</Text></div>
              <div><Text type="secondary" style={{ fontSize: 11 }}>AWB</Text><br /><Text>{addFoundOrder.shipments?.[0]?.awb_code || "—"}</Text></div>
              <div><Text type="secondary" style={{ fontSize: 11 }}>Courier</Text><br /><Text>{addFoundOrder.shipments?.[0]?.courier_name || "—"}</Text></div>
            </div>
          </Card>
        )}
        {addFoundOrder && (
          <div style={{ display: "grid", gap: 10 }}>
            {/* Action Type Selection */}
            <div>
              <Text style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Action Type *</Text>
              <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                <div onClick={() => setAddForm({ ...addForm, actionType: "replacement" })} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: addForm.actionType === "replacement" ? "2px solid #0f766e" : "1px solid #e2e8f0", background: addForm.actionType === "replacement" ? "#f0fdfa" : "#fff", cursor: "pointer", textAlign: "center" }}>
                  <SwapOutlined style={{ fontSize: 18, color: "#0f766e" }} /><br /><Text strong style={{ fontSize: 12 }}>Replacement</Text>
                </div>
                <div onClick={() => setAddForm({ ...addForm, actionType: "credit_note" })} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: addForm.actionType === "credit_note" ? "2px solid #d97706" : "1px solid #e2e8f0", background: addForm.actionType === "credit_note" ? "#fffbeb" : "#fff", cursor: "pointer", textAlign: "center" }}>
                  <MailOutlined style={{ fontSize: 18, color: "#d97706" }} /><br /><Text strong style={{ fontSize: 12 }}>Credit Note</Text>
                </div>
                <div onClick={() => setAddForm({ ...addForm, actionType: "refund" })} style={{ flex: 1, padding: "10px 12px", borderRadius: 8, border: addForm.actionType === "refund" ? "2px solid #db2777" : "1px solid #e2e8f0", background: addForm.actionType === "refund" ? "#fdf2f8" : "#fff", cursor: "pointer", textAlign: "center" }}>
                  <WalletOutlined style={{ fontSize: 18, color: "#db2777" }} /><br /><Text strong style={{ fontSize: 12 }}>Refund</Text>
                </div>
              </div>
            </div>
            <div><Text style={{ fontSize: 11, color: "#64748b" }}>Issue Note * <Text type="danger">(Required)</Text></Text><TextArea rows={2} placeholder={`Why is this ${addForm.actionType === "credit_note" ? "Credit Note" : addForm.actionType === "refund" ? "Refund" : "Replacement"} needed?`} value={addForm.issue} onChange={(e) => setAddForm({ ...addForm, issue: e.target.value })} style={{ marginTop: 4 }} /></div>
            {addForm.actionType === "replacement" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div><Text style={{ fontSize: 11, color: "#64748b" }}>New Order ID *</Text><Input placeholder="Required" value={addForm.newOrderID} onChange={(e) => setAddForm({ ...addForm, newOrderID: e.target.value })} style={{ marginTop: 4 }} /></div>
                <div><Text style={{ fontSize: 11, color: "#64748b" }}>New AWB *</Text><Input placeholder="Required" value={addForm.newAWB} onChange={(e) => setAddForm({ ...addForm, newAWB: e.target.value })} style={{ marginTop: 4 }} /></div>
                <div><Text style={{ fontSize: 11, color: "#64748b" }}>New Carrier *</Text><Select placeholder="Select" value={addForm.newCarrierName || undefined} onChange={(v) => setAddForm({ ...addForm, newCarrierName: v })} options={CARRIERS.map((c) => ({ label: c, value: c }))} style={{ width: "100%", marginTop: 4 }} /></div>
                <div><Text style={{ fontSize: 11, color: "#64748b" }}>New Value (₹)</Text><Input type="number" placeholder="0" value={addForm.newValue} onChange={(e) => setAddForm({ ...addForm, newValue: e.target.value })} style={{ marginTop: 4 }} /></div>
              </div>
            )}
            {addForm.actionType !== "replacement" && (
              <Card size="small" style={{ background: addForm.actionType === "credit_note" ? "#fffbeb" : "#fdf2f8", borderColor: addForm.actionType === "credit_note" ? "#fde68a" : "#fbcfe8" }}>
                <Text strong>{addForm.actionType === "credit_note" ? "📝 Credit Note — No new order required" : "💰 Refund — No new order required"}</Text>
                <Text type="secondary" style={{ display: "block", marginTop: 4, fontSize: 12 }}>This will be sent for approval with the issue note above.</Text>
              </Card>
            )}
            <div><Text style={{ fontSize: 11, color: "#64748b" }}>Note</Text><TextArea rows={2} placeholder="Optional" value={addForm.replacementNote} onChange={(e) => setAddForm({ ...addForm, replacementNote: e.target.value })} style={{ marginTop: 4 }} /></div>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
          <Button type="primary" icon={<CheckCircleOutlined />} loading={addSaving} onClick={handleAddOrder} disabled={!addFoundOrder} style={{ background: "#0f766e", borderColor: "#0f766e" }}>Create</Button>
        </div>
      </Modal>

      {/* WIZARD DRAWER */}
      <Drawer title={<div style={{ display: "flex", alignItems: "center", gap: 8 }}><SwapOutlined style={{ color: "#0f766e" }} /><span>Replacement Process</span>{wizardOrders.length > 0 && <Tag color="blue">{wizardIndex + 1} / {wizardOrders.length}</Tag>}</div>}
        open={wizardOpen} onClose={() => setWizardOpen(false)} width={isMobile ? "100%" : 600}
        footer={currentWizardOrder && (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Button onClick={() => { if (wizardIndex > 0) { setWizardIndex(wizardIndex - 1); setWizardForm({ newOrderID: "", newAWB: "", newCarrierName: "", replacementNote: "", newValue: "", isRefundOnly: false, isCreditNoteOnly: false, isRejected: false, isCanceled: false }); } }} disabled={wizardIndex === 0}>← Previous</Button>
            <Button type="primary" icon={<CheckCircleOutlined />} loading={processingSingle} onClick={processCurrentOrder} style={{ background: "#0f766e", borderColor: "#0f766e" }}>
              {wizardForm.isRefundOnly ? "Complete Refund" : wizardForm.isCreditNoteOnly ? "Complete Credit Note" : wizardForm.isRejected ? "Mark Rejected" : wizardForm.isCanceled ? "Mark Canceled" : wizardIndex === wizardOrders.length - 1 ? "Complete" : "Process & Next →"}
            </Button>
          </div>
        )}>
        {currentWizardOrder && (
          <div className="rs-wizard">
            <div className="rs-wizard-progress"><Progress percent={Math.round(((wizardIndex + 1) / wizardOrders.length) * 100)} status="active" strokeColor={{ "0%": "#0891b2", "100%": "#0f766e" }} /><Text type="secondary" style={{ fontSize: 12 }}>Order {wizardIndex + 1} of {wizardOrders.length}</Text></div>
            <div className="rs-wizard-order-info">
              <div className="info-row"><span className="label">Old Order ID</span><span className="value">{currentWizardOrder.oldOrderID}</span></div>
              <div className="info-row"><span className="label">Old AWB</span><span className="value">{currentWizardOrder.oldAWB || "—"}</span></div>
              <div className="info-row"><span className="label">Carrier</span><span className="value">{currentWizardOrder.carrierName || "—"}</span></div>
              <div className="info-row"><span className="label">Customer</span><span className="value">{currentWizardOrder.customer?.name || "—"}</span></div>
              <div className="info-row"><span className="label">Value</span><span className="value" style={{ color: "#059669" }}>{currentWizardOrder.totalValue ? `₹${currentWizardOrder.totalValue}` : "—"}</span></div>
            </div>
            <div className="rs-wizard-skip"><Text strong style={{ fontSize: 13, color: "#991b1b" }}>Skip new order?</Text>
              <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                <Checkbox checked={wizardForm.isRefundOnly} onChange={(e) => setWizardForm({ ...wizardForm, isRefundOnly: e.target.checked, isCreditNoteOnly: false, isRejected: false, isCanceled: false })}>Refund only</Checkbox>
                <Checkbox checked={wizardForm.isCreditNoteOnly} onChange={(e) => setWizardForm({ ...wizardForm, isCreditNoteOnly: e.target.checked, isRefundOnly: false, isRejected: false, isCanceled: false })}>Credit Note only</Checkbox>
                <Checkbox checked={wizardForm.isRejected} onChange={(e) => setWizardForm({ ...wizardForm, isRejected: e.target.checked, isRefundOnly: false, isCreditNoteOnly: false, isCanceled: false })}>Rejected</Checkbox>
                <Checkbox checked={wizardForm.isCanceled} onChange={(e) => setWizardForm({ ...wizardForm, isCanceled: e.target.checked, isRefundOnly: false, isCreditNoteOnly: false, isRejected: false })}>Canceled</Checkbox>
              </div>
            </div>
            {!wizardForm.isRefundOnly && !wizardForm.isCreditNoteOnly && !wizardForm.isRejected && !wizardForm.isCanceled && (
              <div className="rs-wizard-form">
                <Text strong style={{ fontSize: 14, marginBottom: 10, display: "block" }}>New Order Details</Text>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div><Text style={{ fontSize: 11, color: "#64748b" }}>New Order ID *</Text><Input placeholder="Required" value={wizardForm.newOrderID} onChange={(e) => setWizardForm({ ...wizardForm, newOrderID: e.target.value })} style={{ marginTop: 4 }} /></div>
                  <div><Text style={{ fontSize: 11, color: "#64748b" }}>New AWB *</Text><Input placeholder="Required" value={wizardForm.newAWB} onChange={(e) => setWizardForm({ ...wizardForm, newAWB: e.target.value })} style={{ marginTop: 4 }} /></div>
                  <div><Text style={{ fontSize: 11, color: "#64748b" }}>New Carrier *</Text><Select placeholder="Select" value={wizardForm.newCarrierName || undefined} onChange={(v) => setWizardForm({ ...wizardForm, newCarrierName: v })} options={CARRIERS.map((c) => ({ label: c, value: c }))} style={{ width: "100%", marginTop: 4 }} /></div>
                  <div><Text style={{ fontSize: 11, color: "#64748b" }}>New Value (₹)</Text><Input type="number" placeholder="0" value={wizardForm.newValue} onChange={(e) => setWizardForm({ ...wizardForm, newValue: e.target.value })} style={{ marginTop: 4 }} /></div>
                </div>
                <div style={{ marginTop: 10 }}><Text style={{ fontSize: 11, color: "#64748b" }}>Note</Text><TextArea rows={2} placeholder="Optional" value={wizardForm.replacementNote} onChange={(e) => setWizardForm({ ...wizardForm, replacementNote: e.target.value })} style={{ marginTop: 4 }} /></div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ReplacementSheetPage;
